import { EntityType } from '@frontend/common';
import { EmailInput, PasswordInput } from '@frontend/components/basic-forms';
import { DefaultPrimaryButton } from '@frontend/components/common';
import { ErrorHandler } from '@frontend/error-handler';
import { ICONS } from '@frontend/icons';
import { IdentificationBasicAuthClient, IdentificationClient, MyIdentificationClient } from '@frontend/identification/api';
import { ChangeBasicAuthPassword, Identification, UpdateIdentification } from '@frontend/identification/types';
import { CommonMessage } from '@frontend/lang';
import { useModals } from '@frontend/modal';
import { ToastUtil } from '@frontend/toast-utils';
import type { MouseEvent } from 'react';
import { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import UpdateModal from '../../../../modal/update-modal/update-modal.component';

export interface BasicAuthUpdateProps {
    basic_auth: Identification;
    user_id: string;
    updateMode?: 'patch' | 'change-password';
    selfService?: boolean;
    onSubmitSuccess?: (value: Identification) => void;
}

export const BasicAuthUpdate = (props: BasicAuthUpdateProps) => {
    if (props.selfService || props.updateMode !== 'change-password') {
        return <BasicAuthPatchUpdate {...props} />;
    }

    return <BasicAuthChangePasswordUpdate {...props} />;
};

const BasicAuthPatchUpdate = (props: BasicAuthUpdateProps) => (
    <UpdateModal<Identification, UpdateIdentification>
        order={['key', 'secret']}
        object={props.basic_auth}
        values={{ key: props.basic_auth.key, secret: null }}
        keyOverwrite={
            new Map([
                ['key', CommonMessage.FORMS.EMAIL()],
                ['secret', CommonMessage.FORMS.PASSWORD]
            ])
        }
        onSubmit={(value) =>
            props.selfService
                ? MyIdentificationClient.patchIdentification(props.basic_auth.account_id, props.basic_auth.id, value)
                : IdentificationClient.patchIdentification(props.basic_auth.account_id, props.basic_auth.id, value)
        }
        type={EntityType.IDENTIFICATION}
        label={
            <FormattedMessage
                id='basic_auth.update.title'
                defaultMessage='Update basic authentication'
            />
        }
        onSubmitSuccess={props.onSubmitSuccess}
        successMessageOverwrite={
            <FormattedMessage
                id='basic_auth.update.success'
                defaultMessage='Basic authentication updated successfully'
            />
        }
    />
);

const BasicAuthChangePasswordUpdate = (props: BasicAuthUpdateProps) => {
    const { closeTopModal } = useModals();
    const [formValues, setFormValues] = useState<ChangeBasicAuthPassword>({
        email: props.basic_auth.key,
        old_password: '',
        new_password: ''
    });
    const [emailValid, setEmailValid] = useState<boolean>(true);
    const [oldPasswordValid, setOldPasswordValid] = useState<boolean>(false);
    const [newPasswordValid, setNewPasswordValid] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const submitDisabled = useMemo(
        () =>
            isSubmitting || !emailValid || !oldPasswordValid || !newPasswordValid || !formValues.email || !formValues.old_password || !formValues.new_password,
        [emailValid, formValues.email, formValues.new_password, formValues.old_password, isSubmitting, newPasswordValid, oldPasswordValid]
    );

    const handleSubmit = async (e?: MouseEvent<HTMLButtonElement>) => {
        e?.preventDefault();
        if (submitDisabled) return;

        setIsSubmitting(true);
        try {
            const identification = await IdentificationBasicAuthClient.changePassword(props.basic_auth.account_id, props.user_id, formValues);
            ToastUtil.success(
                CommonMessage.TOAST.PROMISE.SUCCESS,
                <FormattedMessage
                    id='basic_auth.update.success'
                    defaultMessage='Basic authentication updated successfully'
                />
            );
            props.onSubmitSuccess?.(identification);
            closeTopModal();
        } catch (e: any) {
            ErrorHandler.handleError({ error: e, entity_type: CommonMessage.OBJECTS.IDENTIFICATION.DEFAULT });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <form
            className='modal bg-background-default flex h-full flex-col gap-6 rounded-xl p-6'
            onSubmit={(e) => {
                e.preventDefault();
                void handleSubmit();
            }}>
            <div className='modal-header px-0 py-0'>
                <div className='inline-flex w-full items-center justify-between self-stretch'>
                    <div className='text-foreground-default justify-start text-3xl font-semibold'>
                        <FormattedMessage
                            id='basic_auth.update.title'
                            defaultMessage='Update basic authentication'
                        />
                    </div>
                    <button
                        type='button'
                        onClick={closeTopModal}
                        className='bg-background-surface hover:bg-brand-light border-border-default flex size-11 cursor-pointer items-center justify-center self-center rounded-xl border'>
                        <ICONS.BUTTON.CLOSE_2 className='text-brand-muted size-6' />
                    </button>
                </div>
            </div>

            <div className='bg-background-surface border-border-default inline-flex flex-col items-start justify-start gap-3 self-stretch rounded-2xl border p-4'>
                <EmailInput
                    id='basic-auth-email'
                    required
                    label={CommonMessage.FORMS.EMAIL()}
                    value={formValues.email}
                    onChange={(value) => setFormValues((prev) => ({ ...prev, email: value }))}
                    isValidCallback={setEmailValid}
                />
                <PasswordInput
                    id='basic-auth-old-password'
                    required
                    label={
                        <FormattedMessage
                            id='new-password-page.old-password'
                            description='Label for old password input on the new password page'
                            defaultMessage='Old password'
                        />
                    }
                    value={formValues.old_password}
                    onChange={(value) => setFormValues((prev) => ({ ...prev, old_password: value }))}
                    isValidCallback={setOldPasswordValid}
                />
                <PasswordInput
                    id='basic-auth-new-password'
                    required
                    strength
                    noAutoComplete
                    label={
                        <FormattedMessage
                            id='new-password-page.new-password'
                            description='Label for new password input on the new password page'
                            defaultMessage='New password'
                        />
                    }
                    value={formValues.new_password}
                    onChange={(value) => setFormValues((prev) => ({ ...prev, new_password: value }))}
                    isValidCallback={setNewPasswordValid}
                />
            </div>

            <div className='flex w-full items-start justify-end gap-3'>
                <DefaultPrimaryButton
                    text={CommonMessage.BUTTONS.SUBMIT}
                    onClick={handleSubmit}
                    disabled={submitDisabled}
                    isLoading={isSubmitting}
                />
            </div>
        </form>
    );
};
